<template>
  <section class="clinical-note-panel" :class="{ 'clinical-note-panel--submitting': clinicalNote.isSubmitting }">
    <div class="clinical-note-panel__header">
      <h2 class="clinical-note-panel__title">
        {{ $t('app.assessment_mode.text.input.clinical_note_header') }}
      </h2>

      <div v-if="modeButtons.length > 1" class="clinical-note-panel__modes">
        <button
            v-for="mode in modeButtons"
            :key="mode"
            type="button"
            data-analytics-skip
            class="clinical-note-panel__mode-btn"
            :class="{ 'clinical-note-panel__mode-btn--active': mode === 'text' }"
            :disabled="mode === 'text'"
            @click="emitModeChange(mode)"
        >{{ $t(`app.assessment_mode.selection.${mode}.title`) }}</button>
      </div>
    </div>

    <div
        v-if="showLanguageReminder"
        id="clinical-note-language-reminder"
        class="clinical-note-panel__language-reminder"
        role="status"
    >
      <v-icon class="clinical-note-panel__language-reminder-icon" aria-hidden="true">
        {{ mdiInformationOutline }}
      </v-icon>
      <span class="clinical-note-panel__language-reminder-message">
        {{ languageNoticeMessage }}
      </span>
    </div>

    <div class="clinical-note-panel__input-wrap">
      <div
          id="clinical-note"
          ref="editor"
          class="clinical-note-panel__textarea"
          :class="{ 'clinical-note-panel__textarea--locked': !isEditorEditable }"
          :contenteditable="isEditorEditable ? 'true' : 'false'"
          role="textbox"
          aria-multiline="true"
          :aria-label="$t('app.assessment_mode.text.input.label')"
          :aria-disabled="(!isEditorEditable).toString()"
          :aria-describedby="showLanguageReminder ? 'clinical-note-language-reminder' : undefined"
          :data-placeholder="$t('app.assessment_mode.text.input.note_placeholder', 'en')"
          spellcheck="true"
          @input="handleInput"
          @paste="handlePaste"
          @blur="handleBlur"
      ></div>

      <transition name="clinical-note-loading-fade">
        <div
            v-if="clinicalNote.isSubmitting"
            class="clinical-note-panel__loading-overlay"
            role="status"
            aria-live="polite"
            aria-busy="true"
        >
          <div class="clinical-note-panel__loading-orbit" aria-hidden="true">
            <span class="clinical-note-panel__loading-ring" />
            <span class="clinical-note-panel__loading-pulse" />
          </div>
          <div class="clinical-note-panel__loading-copy">
            <strong>{{ $t('app.assessment_mode.text.input.analyzing_note') }}</strong>
          </div>
          <div class="clinical-note-panel__loading-bar" aria-hidden="true">
            <span></span>
          </div>
        </div>
      </transition>
    </div>

    <div
        v-if="showResubmitWarning"
        class="clinical-note-panel__resubmit-warning"
        role="alert"
    >
      <div class="clinical-note-panel__resubmit-warning-content">
        <v-icon class="clinical-note-panel__resubmit-warning-icon" aria-hidden="true">{{ mdiAlertCircleOutline }}</v-icon>
        <span class="clinical-note-panel__resubmit-warning-message">
          {{ $t('app.assessment_mode.text.input.resubmit_warning') }}
        </span>
      </div>
      <div class="clinical-note-panel__resubmit-warning-actions">
        <button
            type="button"
            class="clinical-note-panel__resubmit-warning-btn clinical-note-panel__resubmit-warning-btn--cancel"
            @click="handleResubmitCancel"
        >
          {{ $t('app.assessment_mode.text.input.cancel') }}
        </button>
        <button
            type="button"
            data-analytics-skip
            class="clinical-note-panel__resubmit-warning-btn clinical-note-panel__resubmit-warning-btn--continue"
            @click="handleResubmitContinue"
        >
          {{ $t('app.dialog.button.continue') }}
        </button>
      </div>
    </div>

    <div
        v-else
        class="clinical-note-panel__footer"
    >
      <button
          type="button"
          class="clinical-note-panel__submit"
          :class="{ 'clinical-note-panel__submit--resubmit': clinicalNote.submitted }"
          :disabled="blockedByLanguageAlert || clinicalNote.isSubmitting || (!canSubmit && !clinicalNote.submitted)"
          @click="handleSubmit"
      >
        <template v-if="clinicalNote.isSubmitting">
          {{ $t('app.assessment_mode.text.input.submitting') }}
        </template>
        <template v-else-if="clinicalNote.submitted">
          {{ $t('app.assessment_mode.text.input.resubmit') }}
        </template>
        <template v-else>{{ $t('app.assessment_mode.text.input.submit') }}</template>
      </button>
    </div>
  </section>
</template>

<script lang="ts">
import { computed, defineComponent, nextTick, onMounted, PropType, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { mdiAlertCircleOutline, mdiInformationOutline } from '@mdi/js';
import type { ClinicalNoteState } from '../types/text-mode.types';
import { normalizeForSubmit } from "@/features/text-mode/utils/text-utils";
import { plainTextToPastedHtml, sanitizePastedHtml } from '@/utils/richTextPasteUtils';
import { usePartnerConfigStore } from '@/stores/partnerConfig';
import type { AssessmentModeOption } from '@/features/assessment/types/assessment-mode.types';

export default defineComponent({
  name: 'ClinicalNotePanel',

  props: {
    clinicalNote: {
      type: Object as PropType<ClinicalNoteState>,
      required: true,
    },

    blockedByLanguageAlert: {
      type: Boolean,
      default: false,
    },

    showLanguageReminder: {
      type: Boolean,
      default: false,
    },

    languageNoticeMessage: {
      type: String,
      default: '',
    },
  },

  setup(props, { emit }) {
    const { availableAssessmentModes } = storeToRefs(usePartnerConfigStore());
    const modeButtons = computed<string[]>(() =>
      availableAssessmentModes.value.map((option: AssessmentModeOption) => option.id));

    const editor = ref<HTMLElement | null>(null);
    const cachedEditorHtml = ref('');
    const cachedEditorText = ref('');
    const showResubmitWarning = ref(false);
    const isEditorEditable = computed(() => !props.blockedByLanguageAlert
      && !props.clinicalNote.isSubmitting
      && !props.clinicalNote.submitted);
    const canSubmit = computed(() => Boolean(normalizeForSubmit(props.clinicalNote.text)));

    const emitNoteInput = (value: string): void => {
      emit('note-input', value);
    };

    const emitModeChange = (mode: string): void => {
      emit('mode-change', mode);
    };

    const getEditorText = (): string => {
      return (editor.value?.innerText || editor.value?.textContent || '')
        .replace(/\u00A0/g, ' ')
        .replace(/\u200B/g, '');
    };

    const syncEditorContentFromProps = (): void => {
      const element = editor.value;
      if (!element) return;

      if (!element.innerHTML && props.clinicalNote.text) {
        element.textContent = props.clinicalNote.text;
      }
    };

    const cacheEditorHtml = (): void => {
      const element = editor.value;
      if (!element) return;

      const text = getEditorText();
      const hasText = normalizeForSubmit(text).length > 0;
      cachedEditorText.value = text;
      cachedEditorHtml.value = hasText ? sanitizePastedHtml(element.innerHTML) : '';
    };

    const restoreCachedEditorHtml = (): void => {
      const element = editor.value;
      if (!element) return;

      if (document.activeElement === element && isEditorEditable.value) {
        return;
      }

      if (!normalizeForSubmit(props.clinicalNote.text)) {
        cachedEditorHtml.value = '';
        cachedEditorText.value = '';
        element.innerHTML = '';
        return;
      }

      const cachedTextMatchesState = cachedEditorHtml.value
        && normalizeForSubmit(cachedEditorText.value) === normalizeForSubmit(props.clinicalNote.text);

      if (cachedTextMatchesState && element.innerHTML !== cachedEditorHtml.value) {
        element.innerHTML = cachedEditorHtml.value;
      }
    };

    const syncNoteFromEditor = (): void => {
      cacheEditorHtml();
      emitNoteInput(getEditorText());
    };

    const handleInput = (): void => {
      syncNoteFromEditor();
    };

    const handleSubmit = (): void => {
      if (props.blockedByLanguageAlert) {
        return;
      }

      if (props.clinicalNote.submitted) {
        showResubmitWarning.value = true;
        return;
      }

      cacheEditorHtml();

      const submittedText = normalizeForSubmit(getEditorText());

      if (submittedText.length === 0) {
        return;
      }

      emit('note-submit', submittedText);
    };

    const handleResubmitCancel = (): void => {
      showResubmitWarning.value = false;
    };

    const handleResubmitContinue = (): void => {
      showResubmitWarning.value = false;
      emit('note-resubmit');
    };

    const insertPastedHtml = (html: string): void => {
      if (!editor.value || typeof document === 'undefined') {
        return;
      }

      editor.value.focus();
      document.execCommand('insertHTML', false, html);
    };

    const handlePaste = (event: ClipboardEvent): void => {
      const clipboardData = event.clipboardData;

      if (!isEditorEditable.value || !clipboardData) {
        return;
      }

      event.preventDefault();

      const html = clipboardData.getData('text/html');
      const plainText = clipboardData.getData('text/plain');
      const pastedHtml = (html ? sanitizePastedHtml(html) : '') || plainTextToPastedHtml(plainText);

      if (pastedHtml) {
        insertPastedHtml(pastedHtml);
      }

      syncNoteFromEditor();
    };

    const handleBlur = (): void => {
      if (!isEditorEditable.value || !editor.value) return;

      const sanitizedHtml = sanitizePastedHtml(editor.value.innerHTML);
      if (editor.value.innerHTML !== sanitizedHtml) {
        editor.value.innerHTML = sanitizedHtml;
      }

      syncNoteFromEditor();
    };

    onMounted(syncEditorContentFromProps);

    watch(
      () => [
        props.clinicalNote.text,
        props.clinicalNote.isSubmitting,
        props.clinicalNote.submitted,
      ],
      () => {
        if (!props.clinicalNote.submitted) {
          showResubmitWarning.value = false;
        }
        nextTick(restoreCachedEditorHtml);
      },
    );

    return {
      editor,
      canSubmit,
      isEditorEditable,
      showResubmitWarning,
      mdiAlertCircleOutline,
      mdiInformationOutline,
      modeButtons,
      emitModeChange,
      handleInput,
      handleSubmit,
      handleResubmitCancel,
      handleResubmitContinue,
      handlePaste,
      handleBlur,
    };
  },
});
</script>
